import { useCallback, useRef, useEffect, useState } from 'react'
import type { Asset, GenerationMeta, GeneratedNote, ReferenceAnalysis, CopywritingFormCache } from '@/types'
import type { ReceiptType } from '@/config/receipts'
import { useNotes } from './useNotes'
import { useProfile } from './useProfile'
import { captureError } from '@/lib/sentry.tsx'
import { useCopywritingStore } from '@/stores/copywritingStore'
import { fetchWithAuth } from '@/lib/api'
import { compressBase64 } from '@/lib/compress'
import type { TemplateConfig } from '@/lib/imageUtils'

// ============================================================
// 类型定义（重新导出，供其他组件使用）
// ============================================================
export type { GeneratedNote, ReferenceAnalysis, CopywritingFormCache } from '@/types'

/** 文案风格配置 */
export interface CopywritingStyleConfig {
  id: string
  name: string
  tone: string
  char_min: number
  char_max: number
  hashtag_count: number
}

/** 生成参数（新版） */
export interface GenerateParams {
  referenceText?: string
  referenceAnalysis?: string
  receiptType: ReceiptType
  styleIds: string[]  // 风格 ID 列表
  styles: CopywritingStyleConfig[]  // 完整的风格配置
  background: string
  requirements?: string
  count: number
  assets?: Asset[]
}

/** 参考图分析结果 */
export interface ReferenceImageAnalysis {
  structure: {
    layout: string
    composition: string
    text_placement?: string
  }
  style: {
    color_scheme: string
    mood: string
    visual_elements: string[]
  }
  suggestions: string[]
}

/** 生成结果（来自 API） */
interface GenerateResult {
  notes: Array<{
    title: string
    content: string
    hashtags: string[]
  }>
}

// ============================================================
// 缓存 Key
// ============================================================
const FORM_CACHE_KEY_PREFIX = 'copywriting_form_'
const GENERATED_CACHE_KEY_PREFIX = 'copywriting_generated_'
const ANALYSIS_CACHE_KEY_PREFIX = 'copywriting_analysis_'

function getFormCacheKey(projectId: string) {
  return `${FORM_CACHE_KEY_PREFIX}${projectId}`
}

function getGeneratedCacheKey(projectId: string) {
  return `${GENERATED_CACHE_KEY_PREFIX}${projectId}`
}

function getAnalysisCacheKey(projectId: string) {
  return `${ANALYSIS_CACHE_KEY_PREFIX}${projectId}`
}

// ============================================================
// useCopywriting Hook（重构版）
// ============================================================

export interface UseCopywritingReturn {
  // 状态
  isGenerating: boolean
  isAnalyzing: boolean
  error: string | null
  generatedNotes: GeneratedNote[]
  referenceAnalysis: ReferenceAnalysis | null
  generationProgress: { current: number; total: number } | null  // 生成进度

  // 恢复缓存的生成结果
  loadGeneratedCache: () => void

  // 参考文案分析
  analyzeReference: (text: string) => Promise<ReferenceAnalysis>

  // 生成文案
  generateCopywriting: (params: GenerateParams) => Promise<GeneratedNote[]>

  // 更新单篇笔记（编辑）
  updateGeneratedNote: (id: string, updates: Partial<Pick<GeneratedNote, 'title' | 'content' | 'hashtags'>>) => void

  // 切换选中状态
  toggleNoteSelection: (id: string) => void

  // 全选/取消全选
  toggleSelectAll: () => void

  // 保存选中的笔记到数据库
  saveSelectedNotes: (meta: Omit<GenerationMeta, 'generated_at'>) => Promise<string[]>

  // 清除结果
  clearResults: () => void

  // 表单缓存
  loadFormCache: () => CopywritingFormCache | null
  saveFormCache: (data: CopywritingFormCache) => void
  clearFormCache: () => void

  // 图片相关功能
  redrawImage: (imageUrl: string, instruction: string, style?: string) => Promise<string>
  analyzeReferenceImage: (imageUrl: string) => Promise<ReferenceImageAnalysis>
}

/**
 * AI 文案生成 Hook（重构版）
 * 使用 Zustand 管理状态，支持跨页面持久化
 */
export function useCopywriting(projectId: string): UseCopywritingReturn {
  // 从 Zustand 获取状态
  const {
    isGenerating,
    isAnalyzing,
    error,
    generatedNotes,
    referenceAnalysis,
    generationProgress,
    activeProjectId,
    setGenerating,
    setAnalyzing,
    setError,
    setGeneratedNotes,
    setReferenceAnalysis,
    setGenerationProgress,
    setActiveProject,
    updateGeneratedNote: updateNoteInStore,
    clearResults: clearResultsInStore,
  } = useCopywritingStore()

  // 防止重复调用的时间戳记录
  const lastToggleTimeRef = useRef<Record<string, number>>({})

  const { createNote } = useNotes(projectId)

  // 图片相关 hooks
  const imageRedraw = useImageRedraw()
  const referenceImageAnalysis = useReferenceImageAnalysis()

  // 将当前生成结果写入缓存
  const persistGeneratedCache = useCallback(
    (notes: GeneratedNote[]) => {
      try {
        const payload = JSON.stringify({ notes })
        localStorage.setItem(getGeneratedCacheKey(projectId), payload)
      } catch (e) {
        console.warn('保存生成结果缓存失败:', e)
      }
    },
    [projectId]
  )

  // 将参考分析结果单独写入缓存
  const persistAnalysisCache = useCallback(
    (analysis: ReferenceAnalysis | null) => {
      try {
        if (analysis) {
          localStorage.setItem(getAnalysisCacheKey(projectId), JSON.stringify(analysis))
        } else {
          localStorage.removeItem(getAnalysisCacheKey(projectId))
        }
      } catch (e) {
        console.warn('保存分析结果缓存失败:', e)
      }
    },
    [projectId]
  )

  // 从缓存恢复生成结果
  const loadGeneratedCache = useCallback(() => {
    // 如果已经加载过当前项目的缓存，跳过
    if (activeProjectId === projectId) return
    
    // 加载生成结果
    try {
      const cached = localStorage.getItem(getGeneratedCacheKey(projectId))
      if (cached) {
        const parsed = JSON.parse(cached) as { notes: GeneratedNote[] }
        setGeneratedNotes(parsed.notes || [])
      } else {
        setGeneratedNotes([])
      }
    } catch (e) {
      console.warn('加载生成结果缓存失败:', e)
      localStorage.removeItem(getGeneratedCacheKey(projectId))
      setGeneratedNotes([])
    }
    
    // 加载分析结果（独立缓存）
    try {
      const analysisCached = localStorage.getItem(getAnalysisCacheKey(projectId))
      if (analysisCached) {
        const parsed = JSON.parse(analysisCached) as ReferenceAnalysis
        
        // 数据版本检查
        const isOldFormat = parsed && 'style' in parsed && !('meta_analysis' in parsed)
        if (isOldFormat) {
          console.warn('检测到旧版本分析缓存数据，已清除')
          localStorage.removeItem(getAnalysisCacheKey(projectId))
          setReferenceAnalysis(null)
          return
        }
        
        setReferenceAnalysis(parsed)
      } else {
        setReferenceAnalysis(null)
      }
    } catch (e) {
      console.warn('加载分析结果缓存失败:', e)
      localStorage.removeItem(getAnalysisCacheKey(projectId))
      setReferenceAnalysis(null)
    }

    setActiveProject(projectId)
  }, [projectId, activeProjectId, setGeneratedNotes, setReferenceAnalysis, setActiveProject])

  // 自动加载缓存
  useEffect(() => {
    loadGeneratedCache()
  }, [loadGeneratedCache])

  // 分析参考文案
  const analyzeReference = useCallback(async (text: string): Promise<ReferenceAnalysis> => {
    setAnalyzing(true)
    setError(null)

    try {
      const response = await fetchWithAuth('/api/analyze-reference', {
        method: 'POST',
        body: JSON.stringify({ text }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '分析失败')
      }

      const result: ReferenceAnalysis = await response.json()
      setReferenceAnalysis(result)
      persistAnalysisCache(result)
      return result
    } catch (err) {
      captureError(err, { feature: 'copywriting', action: 'analyze-reference' })
      const message = err instanceof Error ? err.message : '分析失败'
      setError(message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [setAnalyzing, setError, setReferenceAnalysis, persistAnalysisCache])

  // 生成文案（分批生成，8 篇一组）
  const generateCopywriting = useCallback(async (params: GenerateParams): Promise<GeneratedNote[]> => {
    setGenerating(true)
    setError(null)
    setGeneratedNotes([])  // 清空之前的结果

    const BATCH_SIZE = 8
    const totalCount = params.count
    const batches = Math.ceil(totalCount / BATCH_SIZE)
    
    setGenerationProgress({ current: 0, total: totalCount })

    // 在后台执行分批生成
    ;(async () => {
      const allNotes: GeneratedNote[] = []
      
      try {
        for (let i = 0; i < batches; i++) {
          const batchStart = i * BATCH_SIZE
          const batchCount = Math.min(BATCH_SIZE, totalCount - batchStart)
          
          console.log(`[generateCopywriting] 开始生成第 ${i + 1}/${batches} 批，数量: ${batchCount}`)
          
          const response = await fetchWithAuth('/api/generate-copywriting', {
            method: 'POST',
            body: JSON.stringify({
              topic: params.background,
              keywords: '',
              copywriting_styles: params.styles,
              additional_info: params.requirements,
              count: batchCount,
              reference_text: params.referenceText,
              reference_analysis: referenceAnalysis ? JSON.stringify(referenceAnalysis) : undefined,
              assets: params.assets?.map(a => ({
                id: a.id,
                url: a.url,
                description: a.description,
              })),
            }),
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || '生成失败')
          }

          const result: GenerateResult = await response.json()

          const batchNotes: GeneratedNote[] = result.notes.map((note, index) => ({
            ...note,
            id: `gen_${Date.now()}_${batchStart + index}`,
            selected: false,
          }))

          allNotes.push(...batchNotes)
          
          setGeneratedNotes([...allNotes])
          persistGeneratedCache([...allNotes])
          setGenerationProgress({ current: allNotes.length, total: totalCount })
          
          console.log(`[generateCopywriting] 第 ${i + 1}/${batches} 批完成，已生成 ${allNotes.length}/${totalCount} 篇`)
        }
        
        setGenerationProgress(null)
        console.log(`[generateCopywriting] 全部完成，共生成 ${allNotes.length} 篇`)
      } catch (err) {
        captureError(err, { feature: 'copywriting', action: 'generate', noteCount: totalCount })
        const message = err instanceof Error ? err.message : '生成失败'
        setError(message)
        setGenerationProgress(null)
        console.error('[generateCopywriting] 生成失败:', err)
      } finally {
        setGenerating(false)
      }
    })()

    return []
  }, [setGenerating, setError, setGeneratedNotes, setGenerationProgress, referenceAnalysis, persistGeneratedCache])

  // 更新单篇笔记
  const updateGeneratedNote = useCallback((
    id: string,
    updates: Partial<Pick<GeneratedNote, 'title' | 'content' | 'hashtags'>>
  ) => {
    updateNoteInStore(id, updates)
    // 更新后需要同步到本地缓存
    setTimeout(() => {
      const currentNotes = useCopywritingStore.getState().generatedNotes
      persistGeneratedCache(currentNotes)
    }, 0)
  }, [updateNoteInStore, persistGeneratedCache])

  // 切换选中状态
  const toggleNoteSelection = useCallback((id: string) => {
    const now = Date.now()
    const lastTime = lastToggleTimeRef.current[id] || 0
    if (now - lastTime < 100) return
    lastToggleTimeRef.current[id] = now
    
    const note = generatedNotes.find(n => n.id === id)
    if (note && !note.savedNoteId) {
      updateNoteInStore(id, { selected: !note.selected })
      // 同步缓存
      setTimeout(() => {
        const currentNotes = useCopywritingStore.getState().generatedNotes
        persistGeneratedCache(currentNotes)
      }, 0)
    }
  }, [generatedNotes, updateNoteInStore, persistGeneratedCache])

  // 全选/取消全选
  const toggleSelectAll = useCallback(() => {
    const selectableNotes = generatedNotes.filter(note => !note.savedNoteId)
    const allSelected = selectableNotes.length > 0 && selectableNotes.every(note => note.selected)
    
    generatedNotes.forEach(note => {
      if (!note.savedNoteId) {
        updateNoteInStore(note.id, { selected: !allSelected })
      }
    })
    
    // 同步缓存
    setTimeout(() => {
      const currentNotes = useCopywritingStore.getState().generatedNotes
      persistGeneratedCache(currentNotes)
    }, 0)
  }, [generatedNotes, updateNoteInStore, persistGeneratedCache])

  // 保存选中的笔记到数据库
  const saveSelectedNotes = useCallback(async (
    meta: Omit<GenerationMeta, 'generated_at'>
  ): Promise<string[]> => {
    const unsavedSelected = generatedNotes.filter(note => note.selected && !note.savedNoteId)
    
    if (unsavedSelected.length === 0) {
      throw new Error('请至少选择一篇未保存的文案')
    }

    const noteIds: string[] = []
    const fullMeta: GenerationMeta = {
      ...meta,
      generated_at: Date.now(),
    }

    const styleForNote = meta.copywriting_style_ids?.[0] || 'default'

    for (const note of unsavedSelected) {
      const noteId = await createNote({
        title: note.title,
        content: note.content,
        hashtags: note.hashtags,
        style: styleForNote,
        images: [],
        generation_meta: fullMeta,
      })
      noteIds.push(noteId)
      
      // 更新状态
      updateNoteInStore(note.id, {
        selected: false,
        savedNoteId: noteId,
        savedAt: Date.now(),
      })
    }

    // 同步缓存
    setTimeout(() => {
      const currentNotes = useCopywritingStore.getState().generatedNotes
      persistGeneratedCache(currentNotes)
    }, 0)

    return noteIds
  }, [generatedNotes, createNote, updateNoteInStore, persistGeneratedCache])

  // 清除结果
  const clearResults = useCallback(() => {
    clearResultsInStore()
    try {
      localStorage.removeItem(getGeneratedCacheKey(projectId))
    } catch (e) {
      console.warn('清除生成结果缓存失败:', e)
    }
  }, [projectId, clearResultsInStore])

  // 加载表单缓存
  const loadFormCache = useCallback((): CopywritingFormCache | null => {
    try {
      const cached = localStorage.getItem(getFormCacheKey(projectId))
      if (cached) return JSON.parse(cached)
    } catch (e) {
      console.warn('加载表单缓存失败:', e)
    }
    return null
  }, [projectId])

  // 保存表单缓存
  const saveFormCache = useCallback((data: CopywritingFormCache) => {
    try {
      localStorage.setItem(getFormCacheKey(projectId), JSON.stringify(data))
    } catch (e) {
      console.warn('保存表单缓存失败:', e)
    }
  }, [projectId])

  // 清除表单缓存
  const clearFormCache = useCallback(() => {
    try {
      localStorage.removeItem(getFormCacheKey(projectId))
    } catch (e) {
      console.warn('清除表单缓存失败:', e)
    }
  }, [projectId])

  return {
    isGenerating,
    isAnalyzing,
    error,
    generatedNotes,
    referenceAnalysis,
    generationProgress,
    loadGeneratedCache,
    analyzeReference,
    generateCopywriting,
    updateGeneratedNote,
    toggleNoteSelection,
    toggleSelectAll,
    saveSelectedNotes,
    clearResults,
    loadFormCache,
    saveFormCache,
    clearFormCache,
    redrawImage: imageRedraw.redrawImage,
    analyzeReferenceImage: referenceImageAnalysis.analyzeReferenceImage,
  }
}

// ============================================================
// 其他 Hooks（保持不变）
// ============================================================

/**
 * 素材分析 Hook
 */
export function useAssetAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  interface AnalysisResult {
    analyses: Array<{
      id: string
      description: string
      suggested_title?: string
      tags: string[]
      scene: string
      mood: string
      composition?: string[]
      pure_hue?: string
      tonality?: string
    }>
    overall_theme: string
    suggested_topics: string[]
  }

  const analyzeAssets = useCallback(async (
    assets: Array<{ id: string; url: string; filename: string; title?: string }>,
    context?: string
  ): Promise<AnalysisResult> => {
    setIsAnalyzing(true)
    setError(null)

    try {
      const response = await fetchWithAuth('/api/analyze-assets', {
        method: 'POST',
        body: JSON.stringify({ images: assets, context }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '分析失败')
      }

      return await response.json()
    } catch (err) {
      captureError(err, { feature: 'asset-analysis', action: 'analyze' })
      const message = err instanceof Error ? err.message : '分析失败'
      setError(message)
      throw err
    } finally {
      setIsAnalyzing(false)
    }
  }, [])

  return { isAnalyzing, error, analyzeAssets }
}

/**
 * 配图规划 Hook
 */
export function useImagePlanning() {
  const [isPlanning, setIsPlanning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  interface ImagePlan {
    note_id: string
    images: Array<{
      index: number
      type: 'single' | 'collage_2x2' | 'collage_3x1' | 'collage_3x3' | 'ai_generated'
      description: string
      asset_ids?: string[]
      generation_prompt?: string
      image_text?: string
      reference_image?: string
      reference_image_id?: string | string[]
      production_method?: 'asset' | 'collage' | 'ai_generated' | 'collage_then_ai'
    }>
    strategy: string
  }

  interface BatchImagePlan {
    plans: ImagePlan[]
  }

  const planImages = useCallback(async (
    noteId: string,
    title: string,
    content: string,
    assets: Asset[],
    imageCount?: number,
    templateConfig?: TemplateConfig,
    templateConfigs?: TemplateConfig[],
    avoidDuplicates?: boolean,
    customInstruction?: string
  ): Promise<ImagePlan> => {
    setIsPlanning(true)
    setError(null)
    const startTime = Date.now();
    console.log(`[useImagePlanning] 开始规划单篇配图, noteId: ${noteId}`);

    try {
      const response = await fetchWithAuth('/api/plan-images', {
        method: 'POST',
        body: JSON.stringify({
          note_id: noteId,
          title,
          content,
          assets: assets.map(a => ({
            id: a.id,
            url: a.url,
            filename: a.filename,
            title: a.title,
            description: a.description,
            tags: a.tags,
            image_type: a.image_type,
          })),
          image_count: imageCount,
          template_config: templateConfig,
          template_configs: templateConfigs,
          avoidDuplicates,
          custom_instruction: customInstruction,
        }),
      })

      console.log(`[useImagePlanning] API 请求响应, 状态: ${response.status}, 耗时: ${Date.now() - startTime}ms`);

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '规划失败')
      }

      const result: BatchImagePlan = await response.json()
      console.log(`[useImagePlanning] 规划成功, 耗时: ${Date.now() - startTime}ms`);
      return result.plans[0]
    } catch (err) {
      console.error(`[useImagePlanning] 规划失败, 耗时: ${Date.now() - startTime}ms`, err);
      captureError(err, { feature: 'image-planning', action: 'plan-single', noteId })
      const message = err instanceof Error ? err.message : '规划失败'
      setError(message)
      throw err
    } finally {
      setIsPlanning(false)
    }
  }, [])

  const planImagesForMultipleNotes = useCallback(async (
    notes: Array<{ note_id: string; title: string; content: string }>,
    assets: Asset[],
    styleConfig?: { style_id: string },
    imageCount?: number,
    templateConfig?: TemplateConfig,
    templateConfigs?: TemplateConfig[],
    avoidDuplicates?: boolean,
    customInstruction?: string
  ): Promise<ImagePlan[]> => {
    setIsPlanning(true)
    setError(null)
    const startTime = Date.now();
    console.log(`[useImagePlanning] 开始批量规划配图, 笔记数量: ${notes.length}`);

    try {
      const requestBody = JSON.stringify({
        notes,
        assets: assets.map(a => ({
          id: a.id,
          url: a.url,
          filename: a.filename,
          title: a.title,
          description: a.description,
          tags: a.tags,
          image_type: a.image_type,
        })),
        style_config: styleConfig,
        image_count: imageCount,
        template_config: templateConfig,
        template_configs: templateConfigs,
        avoidDuplicates,
        custom_instruction: customInstruction,
      });
      console.log(`[useImagePlanning] 请求体大小: ${requestBody.length} 字节`);
      
      const response = await fetchWithAuth('/api/plan-images', {
        method: 'POST',
        body: requestBody,
      })

      console.log(`[useImagePlanning] API 请求响应, 状态: ${response.status}, 耗时: ${Date.now() - startTime}ms`);
      console.log(`[useImagePlanning] 响应头:`, Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`[useImagePlanning] 响应错误, 状态: ${response.status}, 内容: ${errorText}`);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || '规划失败' };
        }
        throw new Error(errorData.error || '规划失败')
      }

      // 🔥 先获取文本，再解析 JSON，便于调试
      const responseText = await response.text()
      console.log(`[useImagePlanning] 响应体大小: ${responseText.length} 字节`);
      
      if (!responseText) {
        console.error(`[useImagePlanning] 响应体为空!`);
        throw new Error('服务器返回空响应')
      }
      
      let result: BatchImagePlan;
      try {
        result = JSON.parse(responseText);
      } catch (parseErr) {
        console.error(`[useImagePlanning] JSON 解析失败:`, parseErr);
        console.error(`[useImagePlanning] 响应内容预览: ${responseText.slice(0, 500)}`);
        throw new Error('响应数据解析失败')
      }
      
      console.log(`[useImagePlanning] 批量规划成功, 耗时: ${Date.now() - startTime}ms, 返回 ${result.plans?.length || 0} 个规划`);
      return result.plans
    } catch (err) {
      console.error(`[useImagePlanning] 批量规划失败, 耗时: ${Date.now() - startTime}ms`, err);
      captureError(err, { feature: 'image-planning', action: 'plan-batch', noteCount: notes.length })
      const message = err instanceof Error ? err.message : '规划失败'
      setError(message)
      throw err
    } finally {
      setIsPlanning(false)
    }
  }, [])

  return { isPlanning, error, planImages, planImagesForMultipleNotes }
}

/**
 * AI 图片生成 Hook
 */
export function useImageGeneration() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { profile, user } = useProfile()

  const generateImage = useCallback(async (
    prompt: string,
    options?: {
      aspect_ratio?: '1:1' | '3:4' | '4:3' | '16:9' | '9:16'
      image_text?: string
      source_images?: string[]
      reference_image?: string
      note_id?: string
      image_index?: number
      prompt_type?: 'generate-image' | 'generate-image-manual'
    }
  ): Promise<string> => {
    setIsGenerating(true)
    setError(null)

    try {
      const response = await fetchWithAuth('/api/generate-image', {
        method: 'POST',
        body: JSON.stringify({ 
          prompt, 
          ...options,
          org_id: profile?.org_id,
          user_id: user?.id
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '生成失败')
      }

      const result = await response.json()
      if (!result.success) throw new Error(result.error || '生成失败')
      return result.url || result.base64
    } catch (err) {
      captureError(err, { feature: 'image-generation', action: 'generate' })
      const message = err instanceof Error ? err.message : '生成失败'
      setError(message)
      throw err
    } finally {
      setIsGenerating(false)
    }
  }, [profile, user])

  return { isGenerating, error, generateImage }
}

/**
 * AI 图片重绘 Hook
 */
export function useImageRedraw() {
  const [isRedrawing, setIsRedrawing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { profile, user } = useProfile()

  const redrawImage = useCallback(async (
    imageUrl: string,
    instruction: string,
    style?: string
  ): Promise<string> => {
    setIsRedrawing(true)
    setError(null)

    try {
      // 如果是 base64，先压缩
      let finalImageUrl = imageUrl
      if (imageUrl && imageUrl.startsWith('data:')) {
        try {
          finalImageUrl = await compressBase64(imageUrl, 1.0)
        } catch (compressError) {
          console.error('图片压缩失败:', compressError)
        }
      }

      const response = await fetchWithAuth('/api/redraw-image', {
        method: 'POST',
        body: JSON.stringify({ 
          image_url: finalImageUrl, 
          instruction, 
          style,
          org_id: profile?.org_id,
          user_id: user?.id
        }),
      })

      let data
      try {
        data = await response.json()
      } catch (e) {
        if (response.status === 413) {
          throw new Error('图片数据太大，请尝试使用更小的原图')
        }
        throw new Error(`服务器响应异常 (${response.status})`)
      }

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || '重绘失败')
      }

      return data.url || data.base64
    } catch (err) {
      captureError(err, { feature: 'image-redraw', action: 'redraw' })
      const message = err instanceof Error ? err.message : '重绘失败'
      setError(message)
      throw err
    } finally {
      setIsRedrawing(false)
    }
  }, [profile, user])

  return { isRedrawing, error, redrawImage }
}

/**
 * 参考图分析 Hook
 */
export function useReferenceImageAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const analyzeReferenceImage = useCallback(async (
    imageUrl: string
  ): Promise<ReferenceImageAnalysis> => {
    setIsAnalyzing(true)
    setError(null)

    try {
      const response = await fetchWithAuth('/api/analyze-reference-image', {
        method: 'POST',
        body: JSON.stringify({ image_url: imageUrl }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '分析失败')
      }

      return await response.json()
    } catch (err) {
      captureError(err, { feature: 'reference-image', action: 'analyze' })
      const message = err instanceof Error ? err.message : '分析失败'
      setError(message)
      throw err
    } finally {
      setIsAnalyzing(false)
    }
  }, [])

  return { isAnalyzing, error, analyzeReferenceImage }
}
